/* eslint-disable max-lines */
import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { addAddress, deleteAddress, getAddress, getCountries, getStates, getTimezones, updateAddress } from "api";

import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import FormField from "components/molecules/FormField/FormField";
import errorToast from "helpers/errorToast";
import getLabel from "helpers/getLabel";
import Alert from "components/molecules/Alert/Alert";
import testIds from "constants/testIds";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { appendDefaultOption } from "helpers/commonHelpers";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import usePreferredContact from "hooks/usePreferredContact";

const EditAddressBook = function ({ showAddressDialog, closeEditAddressDialog, selectedId, refetch, allowPaymentLocation }) {
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true });
  const { getValues, setValue, handleSubmit, reset, watch } = methods;
  const watchCountry = watch("country");

  const [selectedEditAddressBook, setSelectedEditAddressBook] = useState(null);
  const [countryNameOptions, setCountryNameOptions] = useState([]);
  const [states, setStates] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const preferredContact = selectedEditAddressBook?.preferred_contact || null;
  const { handleContactSearch, handleContactRenderOption, contactSelectOptions, selectedContactDetails } =
    usePreferredContact({ preferredContact, watch });

  const handleCloseDialog = () => {
    reset({
      name: "",
      postal_code: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      location: "",
      country: "",
      state: "",
      timezone: "",
      preferred_contact: null,
    });
    closeEditAddressDialog();
  };

  const defaultOption = [
    {
      value: "",
      label: intl.formatMessage({
        id: "general.companyWide",
        defaultMessage: "Company-wide",
      }),
    },
  ];

  const getAddressDetails = () => {
    setIsLoading(true);
    getAddress(selectedId)
      .then((res) => setSelectedEditAddressBook(res.data))
      .catch((err) => errorToast(err));
  };

  const getStateOptions = () => {
    if (!getValues("country")) return;
    getStates({
      params: {
        country_code: getValues("country"),
      },
    })
      .then((res) => {
        const states = res.data.map(({ value, name, code }) => ({
          label: name,
          code,
          value,
        }));
        setStates(states);
        if (selectedId && states.length > 1)
          setValue("state", states.find((state) => state.label === selectedEditAddressBook.state).value);
      })
      .catch((err) => errorToast(err));
  };

  const getTimeZones = () => {
    if (!getValues("country")) return;
    getTimezones({
      params: {
        country_code: getValues("country"),
      },
    }).then((res) => {
      const availableTimezones = res.data.map(({ value, name }) => ({
        label: name,
        value,
      }));
      setValue("timezone", availableTimezones[0].value, { shouldValidate: true });
      setTimezones(availableTimezones);
      if (selectedId && availableTimezones.length > 0)
        setValue("timezone", availableTimezones[0].value, { shouldValidate: true });
      setIsLoading(false);
    });
  };

  const formatLocations = () => {
    const { locations } = JSON.parse(localStorage.getItem("cargobase_userpermissions")).payment_location;
    const formattedLocations = locations
      .filter((location) => location.is_active)
      .map((location) => ({
        value: location.value,
        label: location.label,
      }));
    if (allowPaymentLocation) return formattedLocations;
    return defaultOption.concat(formattedLocations);
  };

  const prefillEditData = () => {
    setValue("name", selectedEditAddressBook.company_name);
    setValue("postal_code", selectedEditAddressBook.postal_code);
    setValue("address_line_1", selectedEditAddressBook.address_line_1);
    setValue("address_line_2", selectedEditAddressBook.address_line_2);
    setValue("city", selectedEditAddressBook.city);
    setValue("location", selectedEditAddressBook.payment_location.id);
    setValue("country", selectedEditAddressBook.country_iso);

    // Handle preferred contact
    let preferredContactValue = null;
    if (selectedEditAddressBook.preferred_contact) {
      preferredContactValue = selectedEditAddressBook.preferred_contact.id;
    }
    setValue("preferred_contact", preferredContactValue);
  };

  // Always fetch countries upon mount, only get Address Details if selectedId is present
  useEffect(() => {
    setShowConfirmDelete(false);
    getCountries()
      .then((res) => {
        // For now we have to manually remove the country with label "USA" as its causing errors.
        const filteredCountries = res.data.filter((country) => country.label !== "USA");
        setCountryNameOptions(filteredCountries);
      })
      .catch((err) => errorToast(err));
  }, []);

  useEffect(() => {
    if (selectedId) {
      setIsLoading(true);
      getAddressDetails();
    }
  }, [selectedId]);

  // Fetch states and timezones when country changes
  useEffect(() => {
    getStateOptions();
    getTimeZones();
  }, [watchCountry]);

  // Prefill data when the selectedEditAddressBook is updated
  useEffect(() => {
    if (selectedEditAddressBook) prefillEditData();
  }, [selectedEditAddressBook]);

  // Steps incase address is either successfully added or updated.
  const handleSuccess = () => {
    setIsSubmitting(false);
    handleCloseDialog();
    refetch();
    toast(
      intl.formatMessage({
        id: selectedId ? "settings.addressBook.updateSuccess" : "settings.addressBook.addSuccess",
        defaultMessage: selectedId ? "Address successfully updated" : "Address successfully added",
      }),
      {
        type: "success",
      },
    );
  };

  const submitNewAddress = (data) => {
    if (data.payment_location === "" || data.payment_location === "Company-wide") delete data.payment_location;
    addAddress(data)
      .then(() => handleSuccess())
      .catch((err) => {
        setIsSubmitting(false);
        errorToast(err);
      });
  };

  const updateSelectedAddress = (data) => {
    if (data.payment_location === "" || data.payment_location === "Company-wide") delete data.payment_location;
    updateAddress(selectedId, data)
      .then(() => handleSuccess())
      .catch((err) => {
        setIsSubmitting(false);
        const customError = Object.values(err.response.data)[0][0];
        errorToast(err, customError);
      });
  };

  const deleteSelectedAddress = () => {
    setIsDeleting(true);
    deleteAddress(selectedId)
      .then(() => {
        handleCloseDialog();
        refetch(true);
        toast(
          intl.formatMessage({
            id: "settings.addressBook.deleteSuccess",
            defaultMessage: "Address successfully deleted",
          }),
          {
            type: "success",
          },
        );
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsDeleting(false));
  };

  const onSubmit = (data) => {
    setIsSubmitting(true);
    const payload = {
      company_name: data.name,
      country_iso: data.country,
      postal_code: data.postal_code,
      address_line_1: data.address_line_1,
      address_line_2: data.address_line_2,
      city: data.city,
      timezone: data.timezone,
      ...(allowPaymentLocation && { payment_location: data.location }),
      ...(data.state && { state_short: states.find((state) => state.value === parseInt(data.state)).code }),
      preferred_contact_id: data.preferred_contact,
    };
    if (selectedId) updateSelectedAddress(payload);
    else submitNewAddress(payload);
  };

  return (
    <Dialog
      show={showAddressDialog}
      size="md"
      onHide={() => {
        setSelectedEditAddressBook(null);
        reset();
        handleCloseDialog();
      }}
      title={intl.formatMessage({
        id: "settings.address-book.addressDetails",
        defaultMessage: "Address Details",
      })}
      isLoading={isLoading}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {allowPaymentLocation && (
            <Row className="m-0">
              <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
                <div>
                  <FormattedMessage
                    id="general.paymentLocation"
                    values={{ paymentLocation: getLabel("Payment location", "capitalize") }}
                    defaultMessage="Payment location"
                  />
                </div>
              </Col>
              <Col sm={8} className="pr-md-0">
                <div className="input-element">
                  <SearchSelect
                    placeholder={intl.formatMessage({
                      id: "general.pleaseSelect",
                      defaultMessage: "Please select",
                    })}
                    name="location"
                    options={formatLocations()}
                  />
                </div>
              </Col>
            </Row>
          )}

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.name" defaultMessage="Name" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "general.name",
                    defaultMessage: "Name",
                  })}
                  type="textinput"
                  eventContext="Edit Address Dialog"
                  id="name"
                  mandatory
                  field_id="name"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.country" defaultMessage="Country" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <SearchSelect
                  required
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please select",
                  })}
                  name="country"
                  options={appendDefaultOption(countryNameOptions, intl)}
                />
              </div>
            </Col>
          </Row>

          {states.length > 1 && getValues("country") && (
            <Row className="m-0 mt-3">
              <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
                <div className="required">
                  <FormattedMessage id="general.state" defaultMessage="State" />
                </div>
              </Col>
              <Col sm={8} className="pr-md-0">
                <div className="input-element ">
                  <Dropdown
                    useHookForm
                    mandatory
                    placeholder={intl.formatMessage({
                      id: "general.pleaseSelect",
                      defaultMessage: "Please select",
                    })}
                    name="state"
                    options={states}
                  />
                </div>
              </Col>
            </Row>
          )}

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage
                  id="new-request.schedule.addressBookDialog.zipCodeOrPostalCode"
                  defaultMessage="Zip / Postal Code"
                />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.addressBookDialog.zipCodeOrPostalCode",
                    defaultMessage: "Zip / Postal Code",
                  })}
                  type="textinput"
                  mandatory
                  eventContext="Edit Address Dialog"
                  id="postal_code"
                  field_id="postal_code"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.address" defaultMessage="Address" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "general.address",
                    defaultMessage: "Address",
                  })}
                  type="textinput"
                  eventContext="Edit Address Dialog"
                  id="address_line_1"
                  mandatory
                  field_id="address_line_1"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div>
                <FormattedMessage id="new-request.schedule.addressBookDialog.address2" defaultMessage="Address Line 2" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.addressBookDialog.address2",
                    defaultMessage: "Address Line 2",
                  })}
                  type="textinput"
                  eventContext="Edit Address Dialog"
                  id="address_line_2"
                  field_id="address_line_2"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.city" defaultMessage="City" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "general.city",
                    defaultMessage: "City",
                  })}
                  mandatory
                  type="textinput"
                  eventContext="Edit Address Dialog"
                  id="city"
                  field_id="city"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3 mb-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="new-request.schedule.addressBookDialog.timezone" defaultMessage="Timezone" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <Dropdown
                  disabled={!getValues("country")}
                  eventContext="Edit Address Dialog"
                  useHookForm
                  required
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please select",
                  })}
                  name="timezone"
                  options={timezones}
                />
              </div>

              {!getValues("country") && (
                <div className="text-secondary mt-1">
                  <FormattedMessage
                    id="settings.address-book.pleaseSelectCountry"
                    defaultMessage="Please first select a country"
                  />
                </div>
              )}
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div>
                <FormattedMessage id="settings.address-book.preferredContact" defaultMessage="Default Contact Person" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element ">
                <SearchSelect
                  placeholder={intl.formatMessage({
                    id: "general.select",
                    defaultMessage: "Select",
                  })}
                  name="preferred_contact"
                  options={contactSelectOptions}
                  preselect={false}
                  onInputChange={handleContactSearch}
                  handleRenderOption={handleContactRenderOption}
                />
              </div>
            </Col>
          </Row>

          {selectedContactDetails && (
            <Row className="m-0 mt-2 mb-3">
              <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4} />
              <Col sm={8} className="pr-md-0">
                <div className="input-element">
                  <div className="d-flex flex-column">
                    {selectedContactDetails.email && (
                      <div className="d-flex mb-1">
                        <span className="font-weight-bold" style={{ minWidth: "120px" }}>
                          Email:
                        </span>
                        <span className="ml-2">{selectedContactDetails.email}</span>
                      </div>
                    )}
                    {selectedContactDetails.phone && (
                      <div className="d-flex">
                        <span className="font-weight-bold" style={{ minWidth: "120px" }}>
                          Contact Number:
                        </span>
                        <span className="ml-2">{selectedContactDetails.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col sm={12} />
            {!showConfirmDelete && (
              <Col sm={12} className=" mt-3 d-flex justify-content-between">
                <Button
                  className="btn-grey"
                  label={intl.formatMessage({
                    id: "settings.address-book.discardChanges",
                    defaultMessage: "Discard changes",
                  })}
                  onClick={() => {
                    setSelectedEditAddressBook(null);
                    reset();
                    handleCloseDialog();
                  }}
                />
                <div className="d-flex">
                  {selectedId && (
                    <Button
                      className="mr-3"
                      label={intl.formatMessage({
                        id: "settings.address-book.deleteAddress",
                        defaultMessage: "Delete address",
                      })}
                      variant="danger"
                      isLoading={isDeleting}
                      onClick={() => setShowConfirmDelete(true)}
                      data-testid={testIds.ADDRESS_BOOK_DELETE_BUTTON}
                    />
                  )}
                  <Button
                    label={intl.formatMessage({
                      id: "general.save",
                      defaultMessage: "Save",
                    })}
                    className="ml-auto"
                    variant="success"
                    type="submit"
                    isLoading={isSubmitting}
                    data-testid={testIds.ADDRESS_BOOK_SAVE_BUTTON}
                  />
                </div>
              </Col>
            )}
            {showConfirmDelete && (
              <Col sm={12} className="d-flex align-items-center justify-content-between">
                <Alert variant="warning">
                  <FormattedMessage
                    id="settings.address-book.deleteAddressConfirm"
                    defaultMessage="Are you sure you want to delete this address?"
                  />
                </Alert>
                <span className="d-flex">
                  <Button
                    className="btn-grey ml-3"
                    label={intl.formatMessage({
                      id: "general.cancel",
                      defaultMessage: "Cancel",
                    })}
                    onClick={() => setShowConfirmDelete(false)}
                  />
                  <Button
                    className="ml-3"
                    label={intl.formatMessage({
                      id: "general.delete",
                      defaultMessage: "Delete",
                    })}
                    variant="danger"
                    onClick={() => deleteSelectedAddress()}
                  />
                </span>
              </Col>
            )}
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
};

EditAddressBook.propTypes = {
  allowPaymentLocation: PropTypes.bool.isRequired,
  showAddressDialog: PropTypes.bool.isRequired,
  closeEditAddressDialog: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
  refetch: PropTypes.func.isRequired,
};

EditAddressBook.defaultProps = {
  selectedId: null,
};

export default EditAddressBook;
